import React, { useState, useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CContainer,
  CCard,
  CCardHeader,
  CCardBody,
  CSpinner,
  COffcanvas,
  COffcanvasBody,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter,
  CButton,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilSettings, cilMediaPlay, cilMap, cilFolderOpen } from "@coreui/icons";

import { toast } from "react-toastify";
import dayjs from "dayjs";
import { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ru from "date-fns/locale/ru";
import kk from "date-fns/locale/kk";
import { useTranslation } from "react-i18next";
import { saveAs } from "file-saver";

import CameraModals from "./components/CameraModals";
import CameraTabs from "./components/CameraTabs";
import CameraHeader from "./components/CameraHeader";
import CameraFilterPanel from "./components/CameraFilterPanel";
import AnalyticsDashboard from "./components/AnalyticsDashboard";
import CameraTimelineSection from "./components/CameraTimelineSection";
import CameraVideoPlayer from "./components/CameraVideoPlayer";
import CameraArchiveList from "./components/CameraArchiveList";
import CameraExtraModals from "./components/CameraExtraModals";
import GPSMap from "../../components/GPSMap/GPSMap";

import {
  formatDuration,
  getDeviceOffsetMin,
  getFakeLocalDate,
  fromFakeLocalToUtcIso,
} from "./utils/timeUtils";
import { handleExportExcel } from "./utils/exportUtils";
import "./CameraDetailPage.css";

registerLocale("ru", ru);
registerLocale("kk", kk);

const VIDEO_FPS = 24.0;
const videoSecToDbMs = (sec, mapping, fallbackStartMs) => {
  if (!mapping || mapping.length === 0) return fallbackStartMs + sec * 1000;
  const targetF = sec * VIDEO_FPS;
  let l = 0,
    r = mapping.length - 1;
  while (l <= r) {
    const m = Math.floor((l + r) / 2);
    if (mapping[m].f === targetF) return new Date(mapping[m].t).getTime();
    if (mapping[m].f < targetF) l = m + 1;
    else r = m - 1;
  }
  if (l >= mapping.length)
    return new Date(mapping[mapping.length - 1].t).getTime();
  if (r < 0) return new Date(mapping[0].t).getTime();
  const diffL = Math.abs(mapping[l].f - targetF);
  const diffR = Math.abs(mapping[r].f - targetF);
  return new Date(diffL < diffR ? mapping[l].t : mapping[r].t).getTime();
};

const dbMsToVideoSec = (ms, mapping, fallbackStartMs) => {
  if (!mapping || mapping.length === 0)
    return Math.max(0, (ms - fallbackStartMs) / 1000);
  let l = 0,
    r = mapping.length - 1;
  while (l <= r) {
    const m = Math.floor((l + r) / 2);
    const mTime = new Date(mapping[m].t).getTime();
    if (mTime === ms) return mapping[m].f / VIDEO_FPS;
    if (mTime < ms) l = m + 1;
    else r = m - 1;
  }
  if (l >= mapping.length) return mapping[mapping.length - 1].f / VIDEO_FPS;
  if (r < 0) return mapping[0].f / VIDEO_FPS;
  const diffL = Math.abs(new Date(mapping[l].t).getTime() - ms);
  const diffR = Math.abs(new Date(mapping[r].t).getTime() - ms);
  return (diffL < diffR ? mapping[l].f : mapping[r].f) / VIDEO_FPS;
};

const CameraDetailPage = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language ? i18n.language.split("-")[0] : "ru";
  const { cameraId } = useParams();
  const isSuperuser = localStorage.getItem("is_superuser") === "true";
  const isStaff = localStorage.getItem("is_staff") === "true" || isSuperuser;

  const [clickedTimeMs, setClickedTimeMs] = useState(null);
  const [device, setDevice] = useState(null);
  const [trucksList, setTrucksList] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(true);
  const [volumeMode, setVolumeMode] = useState("nominal");
  const [returnToSessionsModal, setReturnToSessionsModal] = useState(false);

  const [offcanvasVisible, setOffcanvasVisible] = useState(false);
  const [videoModalVisible, setVideoModalVisible] = useState(false);

  const [sessionsModalVisible, setSessionsModalVisible] = useState(false);
  const [selectedTruckData, setSelectedTruckData] = useState({ label: "", sessions: [] });
  const [sessionSortBy, setSessionSortBy] = useState("time_asc");

  const [selectedVideo, setSelectedVideo] = useState(null);
  const [selectedFolderId, setSelectedFolderId] = useState(null);
  const [currentFolderStartTime, setCurrentFolderStartTime] = useState(null);
  const [currentAbsoluteTime, setCurrentAbsoluteTime] = useState(null);
  const [crossVideo, setCrossVideo] = useState(null);

  const [framesMapping, setFramesMapping] = useState([]);
  const framesMappingRef = useRef([]);
  useEffect(() => {
    framesMappingRef.current = framesMapping;
  }, [framesMapping]);

  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [selectedSessionInfo, setSelectedSessionInfo] = useState(null);

  const canvasRef = useRef(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [trajectoryData, setTrajectoryData] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [analyticsCooldown, setAnalyticsCooldown] = useState(0);

  const [datesInitialized, setDatesInitialized] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [filterMode, setFilterMode] = useState("custom");
  const [datePreset, setDatePreset] = useState("today");
  const [baseDate, setBaseDate] = useState(null);
  const [selectedShiftId, setSelectedShiftId] = useState("");

  const [recalcStartDate, setRecalcStartDate] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [recalcModalVisible, setRecalcModalVisible] = useState(false);
  const [recalculating, setRecalculating] = useState(false);

  const [twinRecalculating, setTwinRecalculating] = useState(false);
  const [viewerModalVisible, setViewerModalVisible] = useState(false);
  const [viewerUrl, setViewerUrl] = useState("");
  const [isModelLoading, setIsModelLoading] = useState(true);
  const [twinPage, setTwinPage] = useState(1);
  const TWIN_ITEMS_PER_PAGE = 5;

  const [twinCycleModal, setTwinCycleModal] = useState({
    visible: false,
    data: null,
  });

  const [archiveRightPanelMode, setArchiveRightPanelMode] = useState("map");

  const [seekToTime, setSeekToTime] = useState(null);
  const [seekTriggerId, setSeekTriggerId] = useState(0);
  const customPlayerRef = useRef(null);

  const [liveData, setLiveData] = useState(null);
  const [liveIntervalId, setLiveIntervalId] = useState(null);
  const [liveImageModalVisible, setLiveImageModalVisible] = useState(false);

  const [detections, setDetections] = useState({});
  const [isDetectionsLoading, setIsDetectionsLoading] = useState(false);
  const [showDetections, setShowDetections] = useState(true);

  const [schedule, setSchedule] = useState({
    shift_start: "00:00", shift_end: "23:59", work_days: 127, frame_width: 640,
    timezone_offset: 0, max_cycle_time_sec: 180, downtime_threshold_sec: 300,
    idle_truck_threshold_sec: 300,
    has_multiple_shifts: false, realtime_stream_enabled: false,
    min_cycles_to_prevent_split: 3, max_cycles_per_session: 20,
    min_truck_cooldown_sec: 300, save_twin_photos: false,
    include_breaks_in_stats: true, ble_detector_threshold: 0.5,
    session_cooldown_sec: 60,
  });

  const [shifts, setShifts] = useState([]);
  const [newShift, setNewShift] = useState({ name: t("camera_settings.default_shift_day", "Дневная"), start_time: "08:00", end_time: "20:00" });
  const [exceptions, setExceptions] = useState([]);
  const [newException, setNewException] = useState({
    startDate: new Date(), endDate: new Date(), is_working: false, is_maintenance: false, description: "", custom_shift_start: "", custom_shift_end: ""
  });
  const [breaks, setBreaks] = useState([]);
  const [newBreak, setNewBreak] = useState({ start_time: "12:00", end_time: "13:00", description: t("camera_settings.default_break", "Обед") });
  const [materials, setMaterials] = useState([]);
  const [newMaterial, setNewMaterial] = useState({
    name: t("camera_materials.default_material", "Грунт"), is_default: false, startDate: new Date(), endDate: new Date(), bulk_density: 1650, true_density: 2600, fill_factor: 1.0, loosening_factor: 1.58
  });

  const [savingSchedule, setSavingSchedule] = useState(false);
  const [activeTab, setActiveTab] = useState("settings");

  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadReport, setUploadReport] = useState(null);

  const [formatModalVisible, setFormatModalVisible] = useState(false);
  const [formatMode, setFormatMode] = useState("all");
  const [formatDate, setFormatDate] = useState(null);
  const [formatConfirmation, setFormatConfirmation] = useState("");
  const [formatting, setFormatting] = useState(false);

  const [exportModalVisible, setExportModalVisible] = useState(false);
  const [exportStart, setExportStart] = useState(new Date());
  const [exportEnd, setExportEnd] = useState(new Date());
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (startDate) setExportStart(new Date(startDate));
    if (endDate) setExportEnd(new Date(endDate));
  }, [startDate, endDate]);

  const handleOpenExportModal = () => {
    if (startDate) setExportStart(new Date(startDate));
    if (endDate) setExportEnd(new Date(endDate));
    setExportModalVisible(true);
  };

  const classIdToMeta = useMemo(() => ({
    0: { label: t("classes.full_bucket"), color: "#04be61" },
    1: { label: t("classes.unload_vehicle"), color: "#8080ff" },
    2: { label: t("classes.unload_ground"), color: "#ffdd00" },
    3: { label: t("classes.empty_bucket"), color: "#ff3366" },
    4: { label: t("classes.not_visible"), color: "#343a40" },

    10: { label: t("classes.down_full"), color: "#1b5e20" },
    11: { label: t("classes.down_vehicle"), color: "#283593" },
    12: { label: t("classes.down_ground"), color: "#e65100" },
    13: { label: t("classes.down_empty"), color: "#b71c1c" },

    "-1": { label: t("classes.no_data"), color: "#546e7a" },
    "-2": { label: "ТО / Выходной", color: "#7b1fa2" },
    "-3": { label: "Перерыв", color: "#00acc1" },

    default: { label: t("classes.unknown"), color: "#455a64" },
  }), [t]);

  const classNameToColor = useMemo(() => ({
    u00001: "#04be61", u00002: "#8080ff", u00003: "#ffdd00", u00004: "#ff3366",
    u00005: "#343a40", u00006: "#00eeff", u00007: "#8c00ff", u00008: "#ff7b00",
    u00009: "#001eff", u00010: "#3c4690",
  }), []);

  const classNameToLabel = useMemo(() => ({
    u00001: t("classes.full_bucket"), u00002: t("classes.unload_vehicle"),
    u00003: t("classes.unload_ground"), u00004: t("classes.empty_bucket"),
    u00005: t("classes.not_visible"), u00006: t("classes.truck_empty"),
    u00007: t("classes.truck_partial"), u00008: t("classes.truck_full_uneven"),
    u00009: t("classes.truck_full_even"), u00010: t("classes.truck_partial_vis"),
  }), [t]);

  const [formatActionTab, setFormatActionTab] = useState("hide");
  const [formatStats, setFormatStats] = useState(null);

  const openFormatModal = async () => {
    setFormatActionTab("hide");
    setFormatConfirmation("");
    setFormatModalVisible(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`/api/devices/${cameraId}/clear-data/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFormatStats(res.data);
    } catch (e) {
      console.warn("Не удалось загрузить статус скрытых папок", e);
    }
  };

  useEffect(() => {
    if (analyticsCooldown > 0) {
      const timer = setTimeout(() => setAnalyticsCooldown(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [analyticsCooldown]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const loadConfig = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/`, { headers });
        setDevice(res.data);
        try {
          const resTrucks = await axios.get("/api/trucks/", { headers });
          setTrucksList(resTrucks.data);
        } catch (e) {
          console.error("Ошибка загрузки самосвалов");
        }
        const offset = res.data.timezone_offset || 0;
        setSchedule({
          shift_start: res.data.shift_start.substring(0, 5),
          shift_end: res.data.shift_end.substring(0, 5),
          work_days: res.data.work_days,
          frame_width: res.data.frame_width || 640,
          timezone_offset: offset,
          max_cycle_time_sec: res.data.max_cycle_time_sec || 180,
          downtime_threshold_sec: res.data.downtime_threshold_sec || 300,
          idle_truck_threshold_sec: res.data.idle_truck_threshold_sec || 300,
          has_multiple_shifts: res.data.has_multiple_shifts || false,
          realtime_stream_enabled: res.data.realtime_stream_enabled || false,
          min_cycles_to_prevent_split: res.data.min_cycles_to_prevent_split || 3,
          max_cycles_per_session: res.data.max_cycles_per_session || 20,
          min_truck_cooldown_sec: res.data.min_truck_cooldown_sec || 300,
          include_breaks_in_stats: res.data.include_breaks_in_stats !== undefined ? res.data.include_breaks_in_stats : true,
          save_twin_photos: res.data.save_twin_photos || false,
          fuel_tank_volume: res.data.fuel_tank_volume || 500,
          ble_detector_threshold: res.data.ble_detector_threshold !== undefined ? res.data.ble_detector_threshold : 0.5,
          session_cooldown_sec: res.data.session_cooldown_sec !== undefined ? res.data.session_cooldown_sec : 60,
        });

        const offsetMin = getDeviceOffsetMin(offset);
        const d = dayjs().utcOffset(offsetMin);
        const nowFake = new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second());
        const startFake = new Date(nowFake); startFake.setHours(0, 0, 0, 0);
        const endFake = new Date(nowFake); endFake.setHours(23, 59, 59, 999);
        const prevFake = new Date(startFake); prevFake.setDate(prevFake.getDate() - 1);

        setStartDate(startFake); setEndDate(endFake); setRecalcStartDate(prevFake);
        setFormatDate(nowFake); setBaseDate(nowFake);
        setNewException((p) => ({ ...p, startDate: nowFake, endDate: nowFake }));
        setNewMaterial((p) => ({ ...p, startDate: nowFake, endDate: nowFake }));
        setDatesInitialized(true);
      } catch (err) {
        toast.error("Ошибка загрузки данных устройства");
      }
    };
    loadConfig();
  }, [cameraId]);

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset === "custom") return;

    const offsetMin = getDeviceOffsetMin(schedule.timezone_offset);
    const d = dayjs().utcOffset(offsetMin);
    const nowFake = new Date(d.year(), d.month(), d.date(), d.hour(), d.minute(), d.second());

    let newStart = new Date(nowFake);
    let newEnd = new Date(nowFake);
    newEnd.setHours(23, 59, 59, 999);

    switch (preset) {
      case "today": newStart.setHours(0, 0, 0, 0); break;
      case "yesterday": newStart.setDate(newStart.getDate() - 1); newStart.setHours(0, 0, 0, 0); newEnd = new Date(newStart); newEnd.setHours(23, 59, 59, 999); break;
      case "last_2_days": newStart.setDate(newStart.getDate() - 1); newStart.setHours(0, 0, 0, 0); break;
      case "last_3_days": newStart.setDate(newStart.getDate() - 2); newStart.setHours(0, 0, 0, 0); break;
      case "last_7_days": newStart.setDate(newStart.getDate() - 6); newStart.setHours(0, 0, 0, 0); break;
      case "last_14_days": newStart.setDate(newStart.getDate() - 13); newStart.setHours(0, 0, 0, 0); break;
      case "last_month": newStart.setMonth(newStart.getMonth() - 1); newStart.setHours(0, 0, 0, 0); break;
      default: return;
    }
    setStartDate(newStart);
    setEndDate(newEnd);
  };

  const handleExportVideo = async () => {
    if (!exportStart || !exportEnd) { toast.warn("Выберите даты начала и окончания."); return; }
    const diffHours = dayjs(exportEnd).diff(dayjs(exportStart), "hour");
    if (diffHours > 120) { toast.warn("Диапазон не должен превышать 5 суток (120 часов)!"); return; }
    if (exportStart >= exportEnd) { toast.warn("Дата окончания должна быть позже даты начала."); return; }

    setIsExporting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `/api/devices/${cameraId}/export-video/`,
        { start_date: fromFakeLocalToUtcIso(exportStart, schedule.timezone_offset), end_date: fromFakeLocalToUtcIso(exportEnd, schedule.timezone_offset) },
        { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "video/mp4" });
      saveAs(blob, `Export_Video_${dayjs().format("YYYYMMDD_HHmm")}.mp4`);
      toast.success("Видео успешно склеено и загружено!");
      setExportModalVisible(false);
    } catch (err) {
      if (err.response && err.response.data && err.response.data.type === "application/json") {
        const reader = new FileReader();
        reader.onload = () => { toast.error(JSON.parse(reader.result).error || "Ошибка при экспорте видео"); };
        reader.readAsText(err.response.data);
      } else {
        toast.error("Ошибка сети или сбой на сервере при склейке видео.");
      }
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    if (!datesInitialized) return;
    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const fetchFolders = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/folders/`, { headers });
        setFolders(res.data);
      } catch (err) { toast.error("Ошибка загрузки архива"); } finally { setLoadingFolders(false); }
    };
    const fetchAvailableDates = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/available-dates/`, { headers });
        setAvailableDates(res.data.map((d) => new Date(d)));
      } catch (err) { toast.error("Ошибка загрузки доступных дат"); }
    };
    const fetchExceptions = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/schedule-exceptions/`, { headers });
        setExceptions(Array.isArray(res.data) ? res.data : res.data.results || []);
      } catch (err) { toast.error("Ошибка загрузки исключений"); }
    };
    const fetchBreaks = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/breaks/`, { headers });
        setBreaks(Array.isArray(res.data) ? res.data : res.data.results || []);
      } catch (err) { toast.error("Ошибка загрузки перерывов"); }
    };
    const fetchShifts = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/shifts/`, { headers });
        setShifts(Array.isArray(res.data) ? res.data : res.data.results || []);
      } catch (err) { toast.error("Ошибка загрузки смен"); }
    };
    const fetchMaterials = async () => {
      try {
        const res = await axios.get(`/api/devices/${cameraId}/materials/`, { headers });
        setMaterials(Array.isArray(res.data) ? res.data : res.data.results || []);
      } catch (err) { toast.error("Ошибка загрузки материалов"); }
    };

    fetchFolders(); fetchAvailableDates(); fetchExceptions(); fetchBreaks(); fetchShifts(); fetchMaterials(); fetchAnalytics();
  }, [datesInitialized, cameraId]);

  const shiftOptions = useMemo(() => {
    if (schedule.has_multiple_shifts) return shifts.map((s) => ({ id: s.id.toString(), name: s.name, start: s.start_time, end: s.end_time }));
    return [{ id: "default", name: t("camera_settings.default_shift_day", "Основная смена"), start: schedule.shift_start, end: schedule.shift_end }];
  }, [schedule, shifts, t]);

  useEffect(() => {
    if (filterMode === "shift" && selectedShiftId && baseDate) {
      const shift = shiftOptions.find((s) => s.id === selectedShiftId);
      if (shift) {
        const startArr = (shift.start || "00:00").split(":");
        const endArr = (shift.end || "23:59").split(":");
        const newStart = new Date(baseDate);
        newStart.setHours(parseInt(startArr[0], 10), parseInt(startArr[1], 10), 0, 0);
        const newEnd = new Date(baseDate);
        newEnd.setHours(parseInt(endArr[0], 10), parseInt(endArr[1], 10), 59, 999);
        if (newEnd <= newStart) newEnd.setDate(newEnd.getDate() + 1);
        setStartDate(newStart); setEndDate(newEnd);
      }
    }
  }, [filterMode, selectedShiftId, baseDate, shiftOptions]);

  const fetchAnalytics = async () => {
    if (!datesInitialized || !startDate || !endDate) return;
    if (analyticsCooldown > 0) {
      toast.warn(`Подождите ${analyticsCooldown} сек. перед повторным запросом`);
      return;
    }
    setLoadingAnalytics(true);
    setAnalyticsData(null);
    setTrajectoryData([]);
    setTwinPage(1);
    try {
      const token = localStorage.getItem("token");
      const params = {
        device_id: cameraId,
        start_date: fromFakeLocalToUtcIso(startDate, schedule.timezone_offset),
        end_date: fromFakeLocalToUtcIso(endDate, schedule.timezone_offset),
      };
      const response = await axios.get("/api/analytics/", { headers: { Authorization: `Bearer ${token}` }, params });
      setAnalyticsData(response.data);
      try {
        const trajResponse = await axios.get(`/api/devices/${cameraId}/trajectory/`, { headers: { Authorization: `Bearer ${token}` }, params });
        setTrajectoryData(trajResponse.data.map((pt) => ({ ...pt, ts: getFakeLocalDate(pt.ts, schedule.timezone_offset) })));
      } catch (err) { console.warn("GPS Trajectory not found"); }
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setLoadingAnalytics(false);
      if (!isSuperuser) setAnalyticsCooldown(10);
    }
  };

  useEffect(() => {
    if (!selectedFolderId) {
      setDetections({});
      setFramesMapping([]);
      return;
    }
    setDetections({});
    setIsDetectionsLoading(true);

    const fetchData = async () => {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      try {
        const detRes = await axios.get(
          `/api/devices/${cameraId}/folders/${selectedFolderId}/detections/`,
          { headers }
        );
        setDetections(detRes.data);
      } catch (err) {
        console.error("Failed to load detections", err);
      }
      try {
        const markupRes = await axios.get(
          `/api/folders/${selectedFolderId}/markup/`,
          { headers }
        );
        setFramesMapping(markupRes.data.frames_mapping || []);
      } catch (err) {
        if (err.response?.status !== 403) {
          console.warn("Markup data not available:", err.message);
        }
      }

      setIsDetectionsLoading(false);
    };

    fetchData();
  }, [selectedFolderId, cameraId]);

  const isNormalizedData = useMemo(() => {
    if (!detections || Object.keys(detections).length === 0) return true;
    let framesChecked = 0;
    for (const key in detections) {
      const frameObjects = detections[key];
      if (frameObjects && frameObjects.length > 0) {
        if (frameObjects.some((obj) => obj[1] > 2.0 || obj[2] > 2.0 || obj[3] > 2.0 || obj[4] > 2.0)) return false;
      }
      if (framesChecked > 50) break;
    }
    return true;
  }, [detections]);

  useEffect(() => {
    let animationFrameId;
    const FPS = 24.0;
    let lastTimeUpdate = 0;

    const renderLoop = () => {
      const video = customPlayerRef.current?.getInternalPlayer();
      const canvas = canvasRef.current;

      if (video && video.readyState >= 2) {
        const now = Date.now();

        if (now - lastTimeUpdate > 250) {
          const fallbackMs = currentFolderStartTime ? currentFolderStartTime.valueOf() : Date.now();
          const currentDbMs = videoSecToDbMs(video.currentTime, framesMappingRef.current, fallbackMs);
          const localDate = getFakeLocalDate(new Date(currentDbMs).toISOString(), schedule.timezone_offset);

          setCurrentAbsoluteTime((prev) => {
            if (!prev || Math.abs(prev.getTime() - localDate.getTime()) > 250) {
              return localDate;
            }
            return prev;
          });
          lastTimeUpdate = now;
        }

        if (canvas) {
          const ctx = canvas.getContext("2d");
          const videoW = video.videoWidth;
          const videoH = video.videoHeight;

          if (!videoW || !videoH) {
            animationFrameId = requestAnimationFrame(renderLoop);
            return;
          }
          if (canvas.width !== videoW || canvas.height !== videoH) {
            canvas.width = videoW;
            canvas.height = videoH;
          }

          ctx.clearRect(0, 0, canvas.width, canvas.height);

          if (showDetections) {
            const currentFrameIdx = Math.round(video.currentTime * FPS);
            const frameObjects = detections[currentFrameIdx] || detections[currentFrameIdx - 1] || detections[currentFrameIdx + 1];

            if (frameObjects && frameObjects.length > 0) {
              const isNormalized = isNormalizedData;
              const aiBaseWidth = schedule.frame_width || 640;
              const aiBaseHeight = aiBaseWidth * (videoH / videoW);
              const lineThick = Math.max(2, videoW / 400);
              const fontSize = Math.max(14, videoW / 60);

              frameObjects.forEach((obj) => {
                const [cls, x1, y1, x2, y2, conf] = obj;
                const color = classNameToColor[cls] || "red";
                let sx, sy, sw, sh;
                if (isNormalized) {
                  sx = x1 * videoW; sy = y1 * videoH; sw = (x2 - x1) * videoW; sh = (y2 - y1) * videoH;
                } else {
                  sx = (x1 / aiBaseWidth) * videoW; sy = (y1 / aiBaseHeight) * videoH; sw = ((x2 - x1) / aiBaseWidth) * videoW; sh = ((y2 - y1) / aiBaseHeight) * videoH;
                }

                ctx.strokeStyle = color;
                ctx.lineWidth = lineThick;
                ctx.strokeRect(sx, sy, sw, sh);

                const label = classNameToLabel[cls] || cls;
                const text = `${label} ${(conf * 100).toFixed(0)}%`;
                ctx.font = `${fontSize}px Arial`;
                const textWidth = ctx.measureText(text).width;
                const padding = fontSize * 0.3;

                ctx.fillStyle = color;
                ctx.fillRect(sx, sy - (fontSize + padding * 2), textWidth + padding * 2, fontSize + padding * 2);
                ctx.fillStyle = "white";
                ctx.fillText(text, sx + padding, sy - padding);
              });
            }
          }
        }
      }
      animationFrameId = requestAnimationFrame(renderLoop);
    };
    renderLoop();
    return () => cancelAnimationFrame(animationFrameId);

  }, [detections, showDetections, classNameToColor, classNameToLabel, currentFolderStartTime, schedule.frame_width, schedule.timezone_offset, isNormalizedData]);

  useEffect(() => {
    if (activeTab === "live" && schedule.realtime_stream_enabled) {
      const fetchLive = async () => {
        try {
          const token = localStorage.getItem("token");
          const res = await axios.get(`/api/devices/${cameraId}/realtime-data/`, { headers: { Authorization: `Bearer ${token}` } });
          setLiveData(res.data);
        } catch (e) { console.error("Ошибка загрузки живых данных", e); }
      };
      fetchLive();
      const id = setInterval(fetchLive, 5000);
      setLiveIntervalId(id);
      return () => clearInterval(id);
    }
    if (liveIntervalId) { clearInterval(liveIntervalId); setLiveIntervalId(null); }
  }, [activeTab, schedule.realtime_stream_enabled, cameraId]);

  const handleVideoSelect = (folder) => {
    if (!folder.video_file) { toast.warn("Видеофайл еще не готов"); return; }
    const startTimeUtc = dayjs(folder.process_after).subtract(1, "hour");
    setCurrentFolderStartTime(startTimeUtc);
    setSelectedVideo(folder.video_file);
    setSelectedFolderId(folder.id);
    setSeekToTime(0);
    setSeekTriggerId(Date.now());
    setCrossVideo(null);
    setVideoModalVisible(true);
  };

  const handleVideoEnded = () => {
    if (!folders || folders.length === 0 || !selectedFolderId) return;

    const validFolders = [...folders]
      .filter((f) => Boolean(f.video_file))
      .sort((a, b) => new Date(a.process_after) - new Date(b.process_after));

    const currentIndex = validFolders.findIndex((f) => f.id === selectedFolderId);

    if (currentIndex !== -1 && currentIndex + 1 < validFolders.length) {
      const nextFolder = validFolders[currentIndex + 1];
      toast.info(`Воспроизведение следующего архива...`);
      handleVideoSelect(nextFolder);
    } else {
      toast.info("Вы досмотрели последнее доступное видео в архиве");
    }
  };

  const handleTimeDoubleClick = async ({ exactTime, item, fromSessions = false }) => {
    if (!exactTime) return;
    setClickedTimeMs(new Date(exactTime).getTime());
    const fakeDate = new Date(exactTime);
    const utcIsoString = fromFakeLocalToUtcIso(fakeDate, schedule.timezone_offset);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`/api/devices/${cameraId}/video-frame/`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          timestamp: utcIsoString,
          include_cross_video: "true",
        },
      });
      const { video_url, seek_seconds, folder_id, cross_video } = response.data;

      if (video_url !== selectedVideo) {
        setFramesMapping([]);
        const folder = folders.find((f) => f.id === folder_id);
        if (folder) {
          const startTimeUtc = dayjs(folder.process_after).subtract(1, "hour");
          setCurrentFolderStartTime(startTimeUtc);
        }
        setSelectedVideo(video_url);
        setSelectedFolderId(folder_id);
      }
      setSeekToTime(seek_seconds);
      setSeekTriggerId(Date.now());
      setCurrentAbsoluteTime(fakeDate);
      
      setCrossVideo(cross_video || null);

      if (fromSessions) {
        setReturnToSessionsModal(true);
      } else {
        setReturnToSessionsModal(false);
      }

      setVideoModalVisible(true);
      if (customPlayerRef.current) customPlayerRef.current.play();
      toast.info(item ? `Перемотка: ${item.label}` : `Перемотка на выбранное время`);
    } catch (err) {
      toast.error(err.response?.data?.error || "Не удалось найти видео для этого момента.");
      if (fromSessions) {
        setSessionsModalVisible(true);
      }
    }
  };

  const handleTwinCycleDoubleClick = (arg1, arg2) => {
    let exactTimeMs = null;
    let item = null;

    if (typeof arg1 === 'object' && arg1 !== null && ('exactTime' in arg1 || 'item' in arg1)) {
      exactTimeMs = arg1.exactTime;
      item = arg1.item;
    } else if (typeof arg1 === 'number') {
      exactTimeMs = arg1;
      item = arg2;
    } else if (arg1 && arg1.originalItem) {
      item = arg1;
    }

    let twinData = item ? (item.originalItem || item) : null;

    if ((!twinData || !twinData.total_volume) && exactTimeMs && analyticsData && analyticsData.twin_data) {
      twinData = analyticsData.twin_data.find((t) => {
        const tMs = getFakeLocalDate(t.timestamp, schedule.timezone_offset).getTime();
        return Math.abs(tMs - exactTimeMs) <= 30000;
      });
    }

    if (twinData && (twinData.total_volume !== undefined || twinData.timestamp)) {
      setTwinCycleModal({
        visible: true,
        data: twinData,
      });
    } else if (exactTimeMs) {
      handleTimeDoubleClick({ exactTime: exactTimeMs });
    }
  };

  const handleRecalculateSubmit = async () => {
    setRecalculating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post("/api/analytics/recalculate/",
        { device_id: cameraId, start_date: fromFakeLocalToUtcIso(recalcStartDate, schedule.timezone_offset) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Пересчет запущен");
      setRecalcModalVisible(false);
    } catch (err) { toast.error("Ошибка при запуске пересчета"); } finally { setRecalculating(false); }
  };

  const handleUploadArchive = async () => {
    if (!uploadFile) return;
    setIsUploading(true); setIsProcessing(false); setUploadProgress(0);
    setUploadReport({ imported: [], skipped: [], errors: [] });
    const formData = new FormData();
    formData.append("file", uploadFile);
    const token = localStorage.getItem("token");
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/devices/${cameraId}/upload-archive/`, true);
    xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(percent);
        if (percent === 100) { setIsUploading(false); setIsProcessing(true); }
      }
    };
    let lastProcessedIndex = 0; let buffer = "";
    xhr.onprogress = () => {
      const currResponse = xhr.responseText;
      const newChunk = currResponse.substring(lastProcessedIndex);
      lastProcessedIndex = currResponse.length;
      buffer += newChunk;
      const lines = buffer.split("\n");
      buffer = lines.pop();
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const data = JSON.parse(line);
          if (data.status === "error") { toast.error(data.message); }
          else if (data.status === "item_imported") { setUploadReport((prev) => ({ ...prev, imported: [...(prev?.imported || []), data.file] })); }
          else if (data.status === "item_skipped") { setUploadReport((prev) => ({ ...prev, skipped: [...(prev?.skipped || []), `${data.file} (${data.reason})`] })); }
          else if (data.status === "item_error") { setUploadReport((prev) => ({ ...prev, errors: [...(prev?.errors || []), `${data.file}: ${data.reason}`] })); }
          else if (data.status === "finished") { toast.success("Обработка архива завершена"); setTimeout(() => window.location.reload(), 2500); }
        } catch (e) { console.error("Ошибка парсинга строки:", line, e); }
      }
    };
    xhr.onload = () => {
      if (xhr.status !== 200) {
        let errMsg = "Ошибка сервера";
        try { errMsg = JSON.parse(xhr.responseText).error || errMsg; } catch (e) { }
        toast.error(errMsg); setIsProcessing(false);
      }
    };
    xhr.onerror = () => { toast.error("Ошибка сети"); setIsUploading(false); setIsProcessing(false); };
    xhr.send(formData);
  };

  const handleClearData = async () => {
    if (!device) return;
    setFormatting(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { action: "hide", mode: formatMode };
      if (formatMode === "date") {
        payload.date = fromFakeLocalToUtcIso(formatDate, schedule.timezone_offset);
      }
      const response = await axios.post(`/api/devices/${cameraId}/clear-data/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message || "Данные успешно скрыты");
      setFormatModalVisible(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при скрытии данных");
    } finally {
      setFormatting(false);
    }
  };

  const handleRestoreData = async () => {
    if (!device) return;
    setFormatting(true);
    try {
      const token = localStorage.getItem("token");
      const payload = { action: "restore", mode: formatMode };
      if (formatMode === "date") {
        payload.date = fromFakeLocalToUtcIso(formatDate, schedule.timezone_offset);
      }
      const response = await axios.post(`/api/devices/${cameraId}/clear-data/`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(response.data.message || "Видимость успешно восстановлена");
      setFormatModalVisible(false);
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.error || "Ошибка при восстановлении данных");
    } finally {
      setFormatting(false);
    }
  };

  const triggerExportExcel = () => {
    handleExportExcel({
      analyticsData, startDate, endDate, t, formatDuration, folders, schedule, device,
      getDeviceOffsetMin, classIdToMeta, setExportingExcel, toast, volumeMode
    });
  };

  const handleSaveSchedule = async () => {
    if (!isStaff) { toast.warn(t("common.role_restricted")); return; }
    setSavingSchedule(true);
    try {
      const token = localStorage.getItem("token");
      const payload = {
        shift_start: schedule.shift_start, shift_end: schedule.shift_end, work_days: schedule.work_days,
        frame_width: schedule.frame_width, timezone_offset: parseInt(schedule.timezone_offset, 10) || 0,
        max_cycle_time_sec: parseInt(schedule.max_cycle_time_sec, 10) || 180,
        downtime_threshold_sec: parseInt(schedule.downtime_threshold_sec, 10) || 300,
        has_multiple_shifts: schedule.has_multiple_shifts,
        idle_truck_threshold_sec: parseInt(schedule.idle_truck_threshold_sec, 10) || 300,
        realtime_stream_enabled: schedule.realtime_stream_enabled,
        min_cycles_to_prevent_split: parseInt(schedule.min_cycles_to_prevent_split, 10) || 3,
        max_cycles_per_session: parseInt(schedule.max_cycles_per_session, 10) || 20,
        save_twin_photos: schedule.save_twin_photos,
        min_truck_cooldown_sec: parseInt(schedule.min_truck_cooldown_sec, 10) || 300,
        fuel_tank_volume: parseInt(schedule.fuel_tank_volume, 10) || 500,
      };

      if (isSuperuser) {
        payload.ble_detector_threshold = parseFloat(schedule.ble_detector_threshold) ?? 0.5;
        payload.session_cooldown_sec = parseInt(schedule.session_cooldown_sec, 10) || 60;
      }

      await axios.patch(`/api/devices/${cameraId}/`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("camera.settings_saved_success"));
    } catch (err) { toast.error(t("common.error")); } finally { setSavingSchedule(false); }
  };

  const handleRealtimeToggle = async (e) => {
    if (!isStaff) return;
    const newValue = e.target.checked;
    setSchedule((prev) => ({ ...prev, realtime_stream_enabled: newValue }));
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`/api/devices/${cameraId}/`, { realtime_stream_enabled: newValue }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newValue ? "Live трансляция включена" : "Live трансляция отключена");
    } catch (err) { setSchedule((prev) => ({ ...prev, realtime_stream_enabled: !newValue })); toast.error("Ошибка при переключении Live режима"); }
  };

  const handleTwinPhotosToggle = async (e) => {
    if (!isStaff) return;
    const newValue = e.target.checked;
    setSchedule((prev) => ({ ...prev, save_twin_photos: newValue }));
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`/api/devices/${cameraId}/`, { save_twin_photos: newValue }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(newValue ? "Сохранение фото двойника включено" : "Сохранение фото двойника отключено");
    } catch (err) { setSchedule((prev) => ({ ...prev, save_twin_photos: !newValue })); toast.error("Ошибка при переключении режима сохранения фото"); }
  };

  const handleScheduleChange = (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setSchedule((prev) => ({ ...prev, [e.target.name]: val }));
  };
  const handleWorkDayChange = (dayIndex) => { setSchedule((prev) => ({ ...prev, work_days: prev.work_days ^ (1 << dayIndex) })); };

  const handleAddException = async () => {
    if (!isStaff) { toast.warn(t("common.role_restricted")); return; }
    try {
      const token = localStorage.getItem("token");
      const data = {
        ...newException, date: dayjs(newException.startDate).format("YYYY-MM-DD"),
        end_date: newException.endDate ? dayjs(newException.endDate).format("YYYY-MM-DD") : dayjs(newException.startDate).format("YYYY-MM-DD"),
        custom_shift_start: newException.custom_shift_start || null, custom_shift_end: newException.custom_shift_end || null,
      };
      const res = await axios.post(`/api/devices/${cameraId}/schedule-exceptions/`, data, { headers: { Authorization: `Bearer ${token}` } });
      setExceptions((prev) => [...prev, res.data].sort((a, b) => new Date(a.date) - new Date(b.date)));
      const nowFake = getFakeLocalDate(null, schedule.timezone_offset);
      setNewException({ startDate: nowFake, endDate: nowFake, is_working: false, is_maintenance: false, description: "", custom_shift_start: "", custom_shift_end: "" });
      toast.success("Исключение добавлено");
    } catch (err) { toast.error(t("common.error")); }
  };
  const handleDeleteException = async (id) => {
    if (!isStaff) { toast.warn(t("common.role_restricted")); return; }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/devices/${cameraId}/schedule-exceptions/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
      setExceptions((prev) => prev.filter((ex) => ex.id !== id));
      toast.success("Исключение удалено");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleAddBreak = async () => {
    if (!isStaff) { toast.warn(t("common.role_restricted")); return; }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`/api/devices/${cameraId}/breaks/`, newBreak, { headers: { Authorization: `Bearer ${token}` } });
      setBreaks((prev) => [...prev, res.data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      setNewBreak({ start_time: "", end_time: "", description: "" });
      toast.success("Перерыв добавлен");
    } catch (err) { toast.error(t("common.error")); }
  };
  const handleDeleteBreak = async (id) => {
    if (!isStaff) { toast.warn(t("common.role_restricted")); return; }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/devices/${cameraId}/breaks/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
      setBreaks((prev) => prev.filter((b) => b.id !== id));
      toast.success("Перерыв удален");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleAddShift = async () => {
    if (!isStaff) { toast.warn(t("common.role_restricted")); return; }
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`/api/devices/${cameraId}/shifts/`, newShift, { headers: { Authorization: `Bearer ${token}` } });
      setShifts((prev) => [...prev, res.data].sort((a, b) => a.start_time.localeCompare(b.start_time)));
      toast.success("Смена добавлена");
    } catch (err) { toast.error(t("common.error")); }
  };
  const handleDeleteShift = async (id) => {
    if (!isStaff) { toast.warn(t("common.role_restricted")); return; }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/devices/${cameraId}/shifts/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
      setShifts((prev) => prev.filter((s) => s.id !== id));
      toast.success("Смена удалена");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleAddMaterial = async () => {
    if (!isStaff) { toast.warn(t("common.role_restricted")); return; }
    try {
      const token = localStorage.getItem("token");
      const data = {
        ...newMaterial,
        start_date: newMaterial.is_default ? null : dayjs(newMaterial.startDate).format("YYYY-MM-DD"),
        end_date: newMaterial.is_default ? null : newMaterial.endDate ? dayjs(newMaterial.endDate).format("YYYY-MM-DD") : dayjs(newMaterial.startDate).format("YYYY-MM-DD"),
      };
      const res = await axios.post(`/api/devices/${cameraId}/materials/`, data, { headers: { Authorization: `Bearer ${token}` } });
      setMaterials((prev) => [...prev, res.data]);
      toast.success("Материал сохранен");
    } catch (err) { toast.error(t("common.error")); }
  };
  const handleDeleteMaterial = async (id) => {
    if (!isStaff) { toast.warn(t("common.role_restricted")); return; }
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/devices/${cameraId}/materials/${id}/`, { headers: { Authorization: `Bearer ${token}` } });
      setMaterials((prev) => prev.filter((m) => m.id !== id));
      toast.success("Материал удален");
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleRecalculateTwin = async () => {
    if (!isStaff) { toast.warn(t("common.role_restricted")); return; }
    setTwinRecalculating(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post("/api/analytics/recalculate-twin/",
        { device_id: cameraId, start_date: fromFakeLocalToUtcIso(startDate, schedule.timezone_offset), end_date: fromFakeLocalToUtcIso(endDate, schedule.timezone_offset) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || "Пересчет запущен");
      fetchAnalytics();
    } catch (err) { toast.error("Ошибка при запуске пересчета двойника"); } finally { setTwinRecalculating(false); }
  };

  const handleFileChange = (e) => {
    setUploadFile(e.target.files[0]);
    setUploadReport(null); setUploadProgress(0); setIsUploading(false); setIsProcessing(false);
  };

  const getPaginationItems = (currentPage, totalPages) => {
    if (totalPages <= 7) return [...Array(totalPages)].map((_, i) => i + 1);
    if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
    if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
  };

  const pieData = useMemo(() => {
    if (!analyticsData) return [];
    const { durations_by_class_sec } = analyticsData;
    return [
      { id: t("classes.full_bucket"), value: durations_by_class_sec.full_bucket, color: classIdToMeta[0].color },
      { id: t("classes.unload_vehicle"), value: durations_by_class_sec.unloading_vehicle, color: classIdToMeta[1].color },
      { id: t("classes.unload_ground"), value: durations_by_class_sec.unloading_ground, color: classIdToMeta[2].color },
      { id: t("classes.empty_bucket"), value: durations_by_class_sec.empty_bucket, color: classIdToMeta[3].color },
      { id: t("classes.not_visible"), value: durations_by_class_sec.partial_visibility, color: classIdToMeta[4].color },
    ].filter((d) => d.value > 0);
  }, [analyticsData, classIdToMeta, t]);

  const timelineDataForVis = useMemo(() => {
    if (!analyticsData || !analyticsData.timeline || !analyticsData.date_range) return [];

    const result = [];
    const rangeStartFake = getFakeLocalDate(analyticsData.date_range.start, schedule.timezone_offset);
    const rangeEndFake = getFakeLocalDate(analyticsData.date_range.end, schedule.timezone_offset);
    let currentTime = rangeStartFake.getTime();

    const baseTimeline = analyticsData.timeline
      .filter(item => item.class_id !== -2 && item.class_id !== -3)
      .sort((a, b) => new Date(a.start) - new Date(b.start));

    const overlays = analyticsData.timeline
      .filter(item => item.class_id === -2 || item.class_id === -3);

    baseTimeline.forEach((item, index) => {
      const fakeStart = getFakeLocalDate(item.start, schedule.timezone_offset);
      let fakeEnd = getFakeLocalDate(item.end, schedule.timezone_offset);

      if (fakeEnd.getTime() === fakeStart.getTime()) {
        fakeEnd = new Date(fakeEnd.getTime() + 1000);
      }

      let startTimeMs = fakeStart.getTime();
      let endTimeMs = fakeEnd.getTime();

      if (startTimeMs < currentTime) {
        startTimeMs = currentTime;
      }
      if (endTimeMs <= startTimeMs) {
        endTimeMs = startTimeMs + 1000;
      }

      if (startTimeMs > currentTime) {
        result.push({
          id: `offline_gap_${index}`,
          start: currentTime,
          end: startTimeMs,
          color: "#8a93a2",
          label: t("classes.offline", "Устройство отключено"),
          originalItem: { class_id: -1 }
        });
      }

      const meta = classIdToMeta[item.class_id] || classIdToMeta.default;

      result.push({
        id: index,
        start: startTimeMs,
        end: endTimeMs,
        color: meta.color,
        label: meta.label,
        originalItem: item
      });

      currentTime = endTimeMs;
    });

    const rangeEndMs = rangeEndFake.getTime();
    if (currentTime < rangeEndMs) {
      result.push({
        id: `offline_gap_end`,
        start: currentTime,
        end: rangeEndMs,
        color: "#8a93a2",
        label: t("classes.offline", "Устройство отключено"),
        originalItem: { class_id: -1 }
      });
    }

    overlays.forEach((item, idx) => {
      const fakeStart = getFakeLocalDate(item.start, schedule.timezone_offset);
      const fakeEnd = getFakeLocalDate(item.end, schedule.timezone_offset);
      const meta = classIdToMeta[item.class_id] || classIdToMeta.default;

      result.push({
        id: `overlay_${idx}`,
        start: fakeStart.getTime(),
        end: fakeEnd.getTime(),
        color: meta.color,
        label: meta.label,
        originalItem: item
      });
    });

    return result;
  }, [analyticsData, schedule.timezone_offset, classIdToMeta, t]);

  const timelineTrucksForVis = useMemo(() => {
    if (!analyticsData || !analyticsData.truck_sessions || !analyticsData.date_range) return [];
    const result = [];
    const rangeStartFake = getFakeLocalDate(analyticsData.date_range.start, schedule.timezone_offset);
    const rangeEndFake = getFakeLocalDate(analyticsData.date_range.end, schedule.timezone_offset);
    let currentTime = rangeStartFake.getTime();
    let unknownCounter = 0;

    const sortedSessions = [...analyticsData.truck_sessions].sort((a, b) => new Date(a.start) - new Date(b.start));

    sortedSessions.forEach((session, index) => {
      const fakeStart = getFakeLocalDate(session.start, schedule.timezone_offset);
      let fakeEnd = getFakeLocalDate(session.end, schedule.timezone_offset);

      if (fakeEnd.getTime() === fakeStart.getTime()) {
        fakeEnd = new Date(fakeEnd.getTime() + 30000);
      }

      let startTimeMs = fakeStart.getTime();
      let endTimeMs = fakeEnd.getTime();

      if (startTimeMs < currentTime) {
        startTimeMs = currentTime;
      }
      if (endTimeMs <= startTimeMs) {
        endTimeMs = startTimeMs + 1000;
      }

      if (startTimeMs > currentTime) {
        result.push({
          id: `wait_${index}`,
          start: currentTime,
          end: startTimeMs,
          color: "#e9ecef",
          label: t("classes.idle_waiting", "Ожидание самосвала"),
          originalItem: { type: "wait" }
        });
      }

      let barColor = session.color;
      if (!session.has_ble || !barColor) {
        barColor = unknownCounter % 2 === 0 ? "#17a2b8" : "#0d6efd";
        unknownCounter++;
      }

      let methodIcon = "";
      if (session.determination_method === "manual") methodIcon = " 🛠️";
      else if (session.determination_method === "gps") methodIcon = " 📡";
      else if (session.determination_method === "ble") methodIcon = " 📶";
      else if (session.determination_method === "imu") methodIcon = " 🚀";

      result.push({
        id: session.id,
        start: startTimeMs,
        end: endTimeMs,
        color: barColor,
        label: `${session.label}${methodIcon} (Ковшей: ${session.cycles})`,
        originalItem: session
      });

      currentTime = endTimeMs;
    });

    const rangeEndMs = rangeEndFake.getTime();
    if (currentTime < rangeEndMs) {
      result.push({
        id: `wait_end`,
        start: currentTime,
        end: rangeEndMs,
        color: "#e9ecef",
        label: t("classes.idle_waiting", "Ожидание самосвала"),
        originalItem: { type: "wait" }
      });
    }
    return result;
  }, [analyticsData, schedule.timezone_offset, t]);

  const timelineTwinForVis = useMemo(() => {
    if (!analyticsData || !analyticsData.twin_data) return [];

    return analyticsData.twin_data.map((twin, index) => {
      const startTime = twin.start_time
        ? getFakeLocalDate(twin.start_time, schedule.timezone_offset).getTime()
        : getFakeLocalDate(twin.timestamp, schedule.timezone_offset).getTime() - 15000;

      const endTime = getFakeLocalDate(twin.timestamp, schedule.timezone_offset).getTime();

      let barColor = "#2eb85c";
      if (!twin.has_soil || twin.total_volume <= 0) {
        barColor = "#f9b115";
      }
      if (twin.has_missing_tooth) {
        barColor = "#e55353";
      }

      return {
        id: `twin_cycle_${index}`,
        start: startTime,
        end: endTime > startTime ? endTime : startTime + 10000,
        color: barColor,
        label: `${Number(twin.total_volume || 0).toFixed(2)} м³`,
        originalItem: twin
      };
    });
  }, [analyticsData, schedule.timezone_offset]);

  const openSessionsModal = (label, sessions) => {
    setSelectedTruckData({ label, sessions }); setSessionSortBy("time_asc"); setSessionsModalVisible(true);
  };

  const sortedSessions = useMemo(() => {
    if (!selectedTruckData.sessions) return [];
    const sorted = [...selectedTruckData.sessions];
    sorted.sort((a, b) => {
      if (sessionSortBy === "time_asc") return new Date(a.start) - new Date(b.start);
      if (sessionSortBy === "time_desc") return new Date(b.start) - new Date(a.start);
      if (sessionSortBy === "cycles_asc") return a.cycles - b.cycles;
      if (sessionSortBy === "cycles_desc") return b.cycles - a.cycles;
      return 0;
    });
    return sorted;
  }, [selectedTruckData.sessions, sessionSortBy]);

  const actualTruckTotals = useMemo(() => {
    let cycles = 0, vol = 0, mass = 0;
    if (analyticsData && analyticsData.truck_stats_summary) {
      Object.values(analyticsData.truck_stats_summary).forEach((truck) => {
        cycles += truck.cycles || 0; vol += Number(truck.vol) || 0; mass += Number(truck.mass) || 0;
      });
    }
    return { cycles, vol, mass };
  }, [analyticsData]);

  const dateRangeForVis = useMemo(() => {
    if (!analyticsData || !analyticsData.date_range) return null;
    return { start: getFakeLocalDate(analyticsData.date_range.start, schedule.timezone_offset), end: getFakeLocalDate(analyticsData.date_range.end, schedule.timezone_offset) };
  }, [analyticsData, schedule.timezone_offset]);

  if (!datesInitialized || loadingFolders || !device) return <div className="text-center p-5"><CSpinner /></div>;

  const totalTwinItems = analyticsData?.twin_data?.length || 0;
  const totalTwinPages = Math.ceil(totalTwinItems / TWIN_ITEMS_PER_PAGE);
  const currentTwinData = analyticsData?.twin_data?.slice((twinPage - 1) * TWIN_ITEMS_PER_PAGE, twinPage * TWIN_ITEMS_PER_PAGE) || [];

  const cameraTabsProps = {
    activeTab, setActiveTab, schedule, handleScheduleChange, handleWorkDayChange, savingSchedule, handleSaveSchedule, shifts, newShift, setNewShift,
    handleAddShift, handleDeleteShift, breaks, newBreak, setNewBreak, handleAddBreak, handleDeleteBreak, exceptions, newException, setNewException,
    handleAddException, handleDeleteException, materials, newMaterial, setNewMaterial, handleAddMaterial, handleDeleteMaterial, trajectoryData, currentAbsoluteTime,
    telemetryData: analyticsData ? {
      temp: [
        { 
          id: "Температура АКБ (°C)", 
          color: "hsl(30, 80%, 50%)", 
          data: analyticsData.telemetry.chart_data.map((p) => ({ 
            x: getFakeLocalDate(p.ts, schedule.timezone_offset), 
            y: p.cpu 
          })) 
        },
        { 
          id: "Реальный CPU (°C)", 
          color: "hsl(0, 90%, 55%)", 
          data: analyticsData.telemetry.chart_data.map((p) => ({ 
            x: getFakeLocalDate(p.ts, schedule.timezone_offset), 
            y: p.cpu_real || p.cpu
          })) 
        }
      ],
      battery: [
        { 
          id: "Заряд АКБ (%)", 
          color: "hsl(120, 70%, 45%)", 
          data: analyticsData.telemetry.chart_data.map((p) => ({ 
            x: getFakeLocalDate(p.ts, schedule.timezone_offset), 
            y: p.batt 
          })) 
        }
      ],
      voltage: [
        { 
          id: "Напряжение АКБ (В)", 
          color: "hsl(280, 75%, 60%)", 
          data: analyticsData.telemetry.chart_data.map((p) => ({ 
            x: getFakeLocalDate(p.ts, schedule.timezone_offset), 
            y: p.voltage || 0.0 
          })) 
        }
      ],
      signal: [
        { 
          id: "Signal (0-4)", 
          color: "hsl(210, 80%, 55%)", 
          data: analyticsData.telemetry.chart_data.map((p) => ({ 
            x: getFakeLocalDate(p.ts, schedule.timezone_offset), 
            y: p.sig 
          })) 
        }
      ],
      summary: analyticsData.telemetry.summary,
    } : null,
    analyticsData, handleRecalculateTwin, twinRecalculating, twinPage, setTwinPage, totalTwinPages, totalTwinItems, currentTwinData, getPaginationItems,
    setViewerUrl, setIsModelLoading, setViewerModalVisible, cameraId, liveData, isStaff, isSuperuser, handleRealtimeToggle, handleTwinPhotosToggle, liveImageModalVisible, setLiveImageModalVisible,
  };

  const cameraModalsProps = {
    device, isStaff, recalcModalVisible, setRecalcModalVisible, recalcStartDate, setRecalcStartDate, handleRecalculateSubmit, recalculating,
    viewerModalVisible, setViewerModalVisible, viewerUrl, isModelLoading, setIsModelLoading, uploadModalVisible, setUploadModalVisible, handleFileChange,
    uploadFile, isUploading, isProcessing, uploadProgress, uploadReport, handleUploadArchive, 
    formatModalVisible, setFormatModalVisible, formatMode, setFormatMode, formatDate, setFormatDate,
    formatConfirmation, setFormatConfirmation,
    formatActionTab, setFormatActionTab, formatStats, handleClearData, handleRestoreData, formatting,
    exportModalVisible, setExportModalVisible, exportStart, setExportStart, exportEnd, setExportEnd, handleExportVideo, isExporting,
  };

  return (
    <div className="bg-light min-vh-100 p-2 p-md-4">
      <CContainer fluid className="dashboard-centered mobile-p-small">
        <CameraHeader deviceName={device.name} organizationName={device.organization_name} t={t} />

        <CCard className="mb-4 border-0 shadow-sm">
          <CCardHeader className="d-flex flex-wrap justify-content-between align-items-center bg-white border-bottom-0 p-3">
            <CameraFilterPanel
              t={t} isStaff={isStaff} isSuperuser={isSuperuser} filterMode={filterMode} setFilterMode={setFilterMode} datePreset={datePreset}
              handlePresetChange={handlePresetChange} startDate={startDate} setStartDate={setStartDate} endDate={endDate}
              setEndDate={setEndDate} availableDates={availableDates} currentLang={currentLang} baseDate={baseDate}
              setBaseDate={setBaseDate} selectedShiftId={selectedShiftId} setSelectedShiftId={setSelectedShiftId}
              shiftOptions={shiftOptions} fetchAnalytics={fetchAnalytics} loadingAnalytics={loadingAnalytics}
              triggerExportExcel={triggerExportExcel} exportingExcel={exportingExcel} setRecalcStartDate={setRecalcStartDate}
              setRecalcModalVisible={setRecalcModalVisible} setFormatConfirmation={setFormatConfirmation}
              setFormatModalVisible={setFormatModalVisible} toast={toast}
              device={device}
              analyticsData={analyticsData}
              analyticsCooldown={analyticsCooldown}
              volumeMode={volumeMode}
              setVolumeMode={setVolumeMode}
              openFormatModal={openFormatModal}
            />
          </CCardHeader>

          <CCardBody>
            {analyticsData && (
              <AnalyticsDashboard
                t={t} currentLang={currentLang} analyticsData={analyticsData} classIdToMeta={classIdToMeta}
                pieData={pieData} formatDuration={formatDuration} actualTruckTotals={actualTruckTotals}
                openSessionsModal={openSessionsModal} device={device} volumeMode={volumeMode}
                onTimeJump={handleTimeDoubleClick}
                timelineComponent={
                  <CameraTimelineSection
                    t={t}
                    dateRangeForVis={dateRangeForVis}
                    timelineDataForVis={timelineDataForVis}
                    timelineTrucksForVis={timelineTrucksForVis}
                    timelineTwinForVis={timelineTwinForVis}
                    useDigitalTwin={Boolean(device?.use_digital_twin || analyticsData?.use_digital_twin)}
                    handleTimeDoubleClick={handleTimeDoubleClick}
                    handleTwinCycleDoubleClick={handleTwinCycleDoubleClick}
                    device={device}
                    currentTimeMs={currentAbsoluteTime ? currentAbsoluteTime.getTime() : clickedTimeMs}
                    trucksList={trucksList}
                    refreshAnalytics={fetchAnalytics}
                    setAnalyticsData={setAnalyticsData}
                    isStaff={isStaff}
                  />
                }
              />
            )}
          </CCardBody>
        </CCard>

        {/* ПЛАВАЮЩИЕ КНОПКИ СПРАВА СНИЗУ */}
        <div className="floating-controls">
          <button
            className="floating-btn floating-btn-video"
            title="Видеоархив"
            onClick={() => setVideoModalVisible(true)}
          >
            <CIcon icon={cilMediaPlay} size="xl" />
          </button>
          <button
            className="floating-btn floating-btn-settings"
            title="Настройки, Телеметрия, Карта"
            onClick={() => setOffcanvasVisible(true)}
          >
            <CIcon icon={cilSettings} size="xl" />
          </button>
        </div>

        {/* ВЫЕЗЖАЮЩАЯ ПАНЕЛЬ НАСТРОЕК (Offcanvas) */}
        <COffcanvas
          placement="end"
          visible={offcanvasVisible}
          onHide={() => setOffcanvasVisible(false)}
          style={{ width: '30vw', minWidth: '400px', maxWidth: '100%', padding: 0, border: 'none' }}
        >
          <COffcanvasBody className="p-0">
            <CameraTabs
              {...cameraTabsProps}
              onClose={() => setOffcanvasVisible(false)}
            />
          </COffcanvasBody>
        </COffcanvas>

        {/* МОДАЛЬНОЕ ОКНО ВИДЕОАРХИВА */}
        <CModal
          size="xl"
          alignment="center"
          className="video-archive-modal"
          visible={videoModalVisible}
          onClose={() => {
            setVideoModalVisible(false);
            setCrossVideo(null);
            if (returnToSessionsModal) {
              setSessionsModalVisible(true);
              setReturnToSessionsModal(false);
            }
          }}
          fullscreen="xl"
        >
          <CModalHeader className="py-2 px-3">
            <CModalTitle className="fs-6 fw-bold">Видеоархив и Траектория</CModalTitle>
          </CModalHeader>
          <CModalBody className="bg-light p-2 d-flex flex-column">
            <div className="d-flex flex-column modal-body-inner">
              <div className="row g-2 modal-top-row align-items-stretch" style={{ minHeight: 0 }}>

                {/* ЛЕВАЯ ЧАСТЬ: ВИДЕОПЛЕЕР */}
                <div className="col-12 col-xl-8 d-flex flex-column modal-video-col">
                  <CameraVideoPlayer
                    t={t}
                    selectedVideo={selectedVideo}
                    showDetections={showDetections}
                    setShowDetections={setShowDetections}
                    setExportModalVisible={handleOpenExportModal}
                    isDetectionsLoading={isDetectionsLoading}
                    customPlayerRef={customPlayerRef}
                    seekToTime={seekToTime}
                    seekTriggerId={seekTriggerId}
                    canvasRef={canvasRef}
                    onEnded={handleVideoEnded}
                    crossVideo={crossVideo}
                    primaryFramesMapping={framesMapping}
                  />
                </div>

                {/* ПРАВАЯ ЧАСТЬ: ПЕРЕКЛЮЧАТЕЛЬ (КАРТА / АРХИВ) */}
                <div className="col-12 col-xl-4 d-flex flex-column modal-side-col">

                  {/* Кнопки переключения */}
                  <div className="d-flex mb-2 bg-white rounded border p-1 shadow-sm flex-shrink-0">
                    <button
                      type="button"
                      className={`btn btn-sm flex-grow-1 fw-bold d-flex align-items-center justify-content-center ${archiveRightPanelMode === 'map' ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setArchiveRightPanelMode('map')}
                      style={{ transition: 'all 0.2s' }}
                    >
                      <CIcon icon={cilMap} className="me-2" /> Карта маршрута
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm flex-grow-1 fw-bold d-flex align-items-center justify-content-center ${archiveRightPanelMode === 'archive' ? 'btn-primary' : 'btn-light'}`}
                      onClick={() => setArchiveRightPanelMode('archive')}
                      style={{ transition: 'all 0.2s' }}
                    >
                      <CIcon icon={cilFolderOpen} className="me-2" /> Файлы архива
                    </button>
                  </div>

                  {/* Контент правой панели */}
                  <div className="flex-grow-1 d-flex flex-column overflow-hidden position-relative modal-side-content">
                    {archiveRightPanelMode === 'map' ? (
                      <CCard className="h-100 border-0 shadow-sm w-100 overflow-hidden">
                        <GPSMap
                          trajectory={trajectoryData}
                          currentTime={currentAbsoluteTime ? currentAbsoluteTime.getTime() : clickedTimeMs}
                          height="100%"
                        />
                      </CCard>
                    ) : (
                      <CameraArchiveList
                        t={t} isStaff={isStaff} toast={toast} setUploadModalVisible={setUploadModalVisible}
                        folders={folders} selectedVideo={selectedVideo} handleVideoSelect={handleVideoSelect}
                        timezoneOffset={schedule.timezone_offset}
                      />
                    )}
                  </div>

                </div>
              </div>

              {/* НИЖНЯЯ ЧАСТЬ: ТАЙМЛАЙНЫ */}
              {analyticsData && (
                <div className="mt-2 flex-shrink-0 modal-timeline-wrapper">
                  <CameraTimelineSection
                    t={t}
                    dateRangeForVis={dateRangeForVis}
                    timelineDataForVis={timelineDataForVis}
                    timelineTrucksForVis={timelineTrucksForVis}
                    timelineTwinForVis={timelineTwinForVis}
                    useDigitalTwin={Boolean(device?.use_digital_twin || analyticsData?.use_digital_twin)}
                    handleTimeDoubleClick={handleTimeDoubleClick}
                    handleTwinCycleDoubleClick={handleTwinCycleDoubleClick}
                    device={device}
                    currentTimeMs={currentAbsoluteTime ? currentAbsoluteTime.getTime() : clickedTimeMs}
                    trucksList={trucksList}
                    refreshAnalytics={fetchAnalytics}
                    setAnalyticsData={setAnalyticsData}
                    isStaff={isStaff}
                  />
                </div>
              )}
            </div>
          </CModalBody>
        </CModal>

        <CameraExtraModals
          sessionsModalVisible={sessionsModalVisible} setSessionsModalVisible={setSessionsModalVisible}
          selectedTruckData={selectedTruckData} sessionSortBy={sessionSortBy} setSessionSortBy={setSessionSortBy}
          sortedSessions={sortedSessions} schedule={schedule} setSelectedSessionInfo={setSelectedSessionInfo}
          setInfoModalVisible={setInfoModalVisible} handleTimeDoubleClick={handleTimeDoubleClick} infoModalVisible={infoModalVisible}
          selectedSessionInfo={selectedSessionInfo} liveImageModalVisible={liveImageModalVisible} setLiveImageModalVisible={setLiveImageModalVisible}
          liveData={liveData} device={device}
          trucksList={trucksList}
          refreshAnalytics={fetchAnalytics}
        />

        {/* МОДАЛЬНОЕ ОКНО ДЕТАЛЕЙ ЦИКЛА ЦИФРОВОГО ДВОЙНИКА */}
        <CModal
          visible={twinCycleModal.visible}
          onClose={() => setTwinCycleModal({ visible: false, data: null })}
          alignment="center"
          size="lg"
        >
          <CModalHeader closeButton className="bg-light">
            <CModalTitle className="fw-bold text-primary">
              Детализация цикла Цифрового Двойника
            </CModalTitle>
          </CModalHeader>
          <CModalBody className="bg-light p-4">
            {twinCycleModal.data && (
              <div className="row g-3">
                <div className="col-12 col-md-6">
                  <div className="p-3 bg-white rounded border shadow-sm h-100">
                    <h6 className="fw-bold border-bottom pb-2 mb-3">Метрики замера</h6>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Время окончания:</span>
                      <strong>{dayjs(twinCycleModal.data.timestamp).format("DD.MM.YYYY HH:mm:ss")}</strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Итоговый объем:</span>
                      <strong className="text-success fs-5">
                        {typeof twinCycleModal.data.total_volume === 'number' ? twinCycleModal.data.total_volume.toFixed(2) : 0} м³
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Расчетная масса:</span>
                      <strong className="text-primary fs-5">
                        {typeof twinCycleModal.data.mass === 'number' ? (twinCycleModal.data.mass / 1000).toFixed(2) : 0} т
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Высота шапки:</span>
                      <strong>
                        +{typeof twinCycleModal.data.soil_height === 'number' ? twinCycleModal.data.soil_height.toFixed(2) : 0} м
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Объем внутри чаши:</span>
                      <strong>
                        {typeof twinCycleModal.data.inner_volume === 'number' ? twinCycleModal.data.inner_volume.toFixed(2) : 0} м³
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Объем шапки:</span>
                      <strong>
                        {typeof twinCycleModal.data.cap_volume === 'number' ? twinCycleModal.data.cap_volume.toFixed(2) : 0} м³
                      </strong>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Состояние зуба:</span>
                      {twinCycleModal.data.has_missing_tooth ? (
                        <span className="badge bg-danger">Отсутствует ({twinCycleModal.data.missing_teeth_count} шт)</span>
                      ) : (
                        <span className="badge bg-success">Все зубья на месте</span>
                      )}
                    </div>
                    {twinCycleModal.data.anomaly_reason && (
                      <div className="mt-3 p-2 bg-warning bg-opacity-10 border border-warning rounded text-dark small">
                        <strong>Примечание:</strong> {twinCycleModal.data.anomaly_reason}
                      </div>
                    )}
                  </div>
                </div>

                <div className="col-12 col-md-6">
                  <div className="p-2 bg-dark rounded border shadow-sm d-flex align-items-center justify-content-center h-100" style={{ minHeight: "220px" }}>
                    {twinCycleModal.data.image_url ? (
                      <img
                        src={twinCycleModal.data.image_url}
                        alt="Cycle Debug"
                        style={{ maxWidth: "100%", maxHeight: "300px", objectFit: "contain", borderRadius: "4px" }}
                      />
                    ) : (
                      <div className="text-white small">Фотозамер отсутствует</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CModalBody>
          <CModalFooter className="bg-light">
            <CButton color="secondary" className="fw-bold px-4" onClick={() => setTwinCycleModal({ visible: false, data: null })}>
              Закрыть
            </CButton>
          </CModalFooter>
        </CModal>

      </CContainer>
      <CameraModals {...cameraModalsProps} />
    </div>
  );
};

export default CameraDetailPage;